import { create } from 'zustand';
import type { Attachment, HelloOkPayload } from '@src/services/claw-ws';
import { listMarioAgentThreads } from '@src/services/community';
import {
  mapMarioThreadsToClawSessions,
  type MarioAgentThreadListRow,
} from '@src/hooks/clawMergedSessionsList';
import { useLayoutStore } from './useLayoutStore';

// ─── 类型 ──────────────────────────────────────────────────────────────────────

export type WsConnStatus = 'idle' | 'connecting' | 'connected' | 'disconnected';
const MARIO_WEBCHAT_KEY_FLAG = 'mario-webchat';

// 判断 sessionkey agent:main:fe4ea54a-d4bd-4d21-987a-d999cab56580 按照:分割，取最后一个
const getSessionKey = (sessionKey: string) => {
  return sessionKey.split(':').pop() ?? '';
};

export const isMarioWebchatSessionKey = (key: string | undefined) => {
  return Boolean(key && key.includes(MARIO_WEBCHAT_KEY_FLAG));
};

const getSessionTs = (session: ClawSession) => {
  return session.updatedAt ?? session.createdAt ?? 0;
};

/** 文本块 */
export interface TextBlock {
  type: 'text';
  text: string;
}

/** 思考块（Claude extended thinking） */
export interface ThinkingBlock {
  type: 'thinking';
  thinking: string;
  thinkingSignature?: string;
  /** 收到该块时的时间戳（ms），用于与 tool call 混合排序；不存在时视为最早（排序为 0） */
  at?: number;
}

export type ContentBlock = TextBlock | ThinkingBlock;

/** agent lifecycle start 事件记录 */
export interface LifecycleEvent {
  runId: string;
  sessionKey: string;
  phase: 'start';
  startedAt: number;
}

/** 工具调用记录（从 agent stream=tool 事件聚合而来） */
export interface ToolCall {
  toolCallId: string;
  name: string;
  sessionKey: string;
  runId: string;
  args?: Record<string, unknown>;
  /** running: start 已收到；done: result 且 isError=false；error: result 且 isError=true */
  status: 'running' | 'done' | 'error';
  /** 仅 verboseLevel=full 时存在 */
  result?: unknown;
  isError?: boolean;
  /** exec 等工具的元信息标记 */
  meta?: string;
  startedAt: number;
  finishedAt?: number;
}

/** content 既可能是普通字符串，也可能是结构化 block 数组 */
export type MessageContent = string | ContentBlock[];

export interface ClawSession {
  key: string;
  label?: string;
  displayName?: string;
  display_name?: string;
  createdAt?: number;
  updatedAt?: number;
  [key: string]: unknown;
}

export interface ClawChatMessage {
  role: 'user' | 'assistant' | 'system';
  sessionKey: string;
  content: MessageContent;
  /** 流式输出时持续追加 delta，done=true 时内容已完整 */
  done: boolean;
  timestamp?: number;
  idempotencyKey?: string;
  /** 消息携带的附件（不含 base64 content，仅用于展示） */
  attachments?: Omit<Attachment, 'content'>[];
}

// ─── State ────────────────────────────────────────────────────────────────────

interface ClawWsState {
  /** WebSocket 连接状态 */
  connStatus: WsConnStatus;
  /** 握手成功后的 hello-ok payload（含协议版本、服务端信息、支持方法/事件列表） */
  helloOk: HelloOkPayload | null;
  /** 会话列表 */
  sessions: ClawSession[];
  /** 会话未读红点（按完整 session key 记录） */
  unreadSessionKeys: string[];
  /** 当前激活会话的 key */
  currentSessionKey: string | null;
  /** 当前会话的消息列表（包含流式中间态） */
  messages: ClawChatMessage[];
  /** 当前 run 产生的工具调用列表（agent stream=tool 事件聚合） */
  toolCalls: ToolCall[];
  /** agent lifecycle start 事件列表（按 runId 索引，用于展示"收到，准备开工..."） */
  lifecycleEvents: LifecycleEvent[];
  /** 会话列表 popover 是否固定到右侧（本地持久化） */
  sessionsPopoverPinned: boolean;
  /** 新门户 threads 接口加载状态 */
  marioThreadsLoading: boolean;

  // ── Actions ──────────────────────────────────────────────────────────────────

  setConnStatus: (status: WsConnStatus) => void;
  setHelloOk: (hello: HelloOkPayload | null) => void;
  setSessions: (sessions: ClawSession[]) => void;
  setCurrentSessionKey: (key: string | null) => void;
  markSessionUnread: (key: string) => void;
  markSessionRead: (key: string) => void;
  clearSessionUnread: () => void;
  /** 拉取新门户 threads，并同步会话与未读状态 */
  fetchMarioAgentThreadsAsClawSessions: () => Promise<ClawSession[]>;

  /** 追加一条新消息 */
  appendMessage: (msg: ClawChatMessage) => void;
  /** 追加流式 delta 到最后一条 assistant 消息；done=true 时标记完成 */
  appendDelta: (sessionKey: string, delta: string, done: boolean) => void;
  /**
   * 将最近一条 done=false 的 assistant 占位消息替换为最终消息。
   * 若找不到占位，则直接追加。
   */
  finishMessage: (msg: ClawChatMessage) => void;
  /** 清空当前会话消息（切换会话时调用） */
  clearMessages: () => void;
  /**
   * 将所有消息的 sessionKey 从 oldKey 更新为 newKey。
   * 用于处理服务端返回的 sessionKey 与客户端生成的不一致（如添加前缀）。
   */
  remapSessionKey: (oldKey: string, newKey: string) => void;
  /**
   * 立即将指定 session 中最后一条 done=false 的 assistant 消息标记为完成。
   * 用于 chat.abort 后停止流式动画，无需等待服务端回包。
   */
  abortStreaming: (sessionKey: string) => void;

  /** 新增一条工具调用记录（phase=start 时调用） */
  appendToolCall: (call: ToolCall) => void;
  /** 更新已有工具调用（phase=result 时调用） */
  updateToolCall: (toolCallId: string, update: Partial<ToolCall>) => void;
  /** 切换会话或 run 结束时清空工具调用列表 */
  clearToolCalls: () => void;
  /** 新增一条 lifecycle start 记录 */
  appendLifecycleEvent: (event: LifecycleEvent) => void;
  /** 清空 lifecycle 记录（切换会话时调用） */
  clearLifecycleEvents: () => void;

  setSessionsPopoverPinned: (pinned: boolean) => void;
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const PIN_STORAGE_KEY = 'claw-sessions-popover-pinned-v1';

function getInitialPinned(): boolean {
  if (
    typeof localStorage === 'undefined' ||
    localStorage.getItem(PIN_STORAGE_KEY) === null
  ) {
    // 顺便收起左侧侧边栏
    // localStorage.setItem(PIN_STORAGE_KEY, 'true');
    return true;
  }
  return localStorage.getItem(PIN_STORAGE_KEY) === 'true';
}

const useClawWsStore = create<ClawWsState>((set) => ({
  connStatus: 'idle',
  helloOk: null,
  sessions: [],
  unreadSessionKeys: [],
  currentSessionKey: null,
  messages: [],
  toolCalls: [],
  lifecycleEvents: [],
  sessionsPopoverPinned: getInitialPinned(),
  marioThreadsLoading: false,

  setConnStatus: (connStatus) => set({ connStatus }),
  setHelloOk: (helloOk) => set({ helloOk }),
  setSessions: (sessions) => set({ sessions }),
  setCurrentSessionKey: (currentSessionKey) =>
    set((s) => ({
      currentSessionKey,
      unreadSessionKeys: currentSessionKey
        ? s.unreadSessionKeys.filter((k) => k !== currentSessionKey)
        : s.unreadSessionKeys,
    })),
  markSessionUnread: (key) =>
    set((s) => {
      if (!key || s.unreadSessionKeys.includes(key)) return s;
      return { unreadSessionKeys: [...s.unreadSessionKeys, key] };
    }),
  markSessionRead: (key) =>
    set((s) => ({
      unreadSessionKeys: s.unreadSessionKeys.filter((k) => k !== key),
    })),
  clearSessionUnread: () => set({ unreadSessionKeys: [] }),
  fetchMarioAgentThreadsAsClawSessions: async (): Promise<ClawSession[]> => {
    set({ marioThreadsLoading: true });
    try {
      const res = await listMarioAgentThreads(
        {},
        { headers: { disabledToast: true } },
      );
      if (!(res?.code === 0 || res?.status === 0) || !Array.isArray(res.data)) {
        return [];
      }
      const threadMarioSessions = mapMarioThreadsToClawSessions(
        res.data as MarioAgentThreadListRow[],
      );
      const state = useClawWsStore.getState();
      const prevSessions = state.sessions;
      const stableNonMarioSessions = prevSessions.filter(
        (session) => !isMarioWebchatSessionKey(session.key),
      );
      const activeKey = state.currentSessionKey;
      const prevMarioSessions = prevSessions.filter((session) =>
        isMarioWebchatSessionKey(session.key),
      );
      const mergedSessions = [...stableNonMarioSessions];
      set((state) => {
        const unreadSet = new Set(state.unreadSessionKeys);

        // 首次拉取不打红点；后续仅对新增会话或更新时间变更的非当前会话打未读。
        if (prevMarioSessions.length > 0) {
          const prevTsMap = new Map<string, number>();
          prevMarioSessions.forEach((session) => {
            prevTsMap.set(session.key, getSessionTs(session));
          });
          threadMarioSessions.forEach((session) => {
            if (!session.key || session.key === activeKey) return;
            const nextTs = getSessionTs(session);
            const prevTs = prevTsMap.get(session.key);
            if (prevTs === undefined || nextTs > prevTs) {
              unreadSet.add(session.key);
            }
          });
        }
        mergedSessions.push(...threadMarioSessions);
        mergedSessions.sort((a, b) => {
          return (
            Number(b.updatedAt ?? b.createdAt ?? 0) -
            Number(a.updatedAt ?? a.createdAt ?? 0)
          );
        });
        if (activeKey) {
          unreadSet.delete(activeKey);
        }
        return {
          sessions: mergedSessions,
          unreadSessionKeys: Array.from(unreadSet),
        };
      });
      return mergedSessions;
    } catch {
      return [];
    } finally {
      set({ marioThreadsLoading: false });
    }
  },

  appendMessage: (msg) => set((s) => ({ messages: [...s.messages, msg] })),

  appendDelta: (sessionKey, delta, done) =>
    set((s) => {
      const msgs = [...s.messages];
      const last = msgs[msgs.length - 1];
      if (
        last &&
        last.role === 'assistant' &&
        getSessionKey(last.sessionKey) === getSessionKey(sessionKey) &&
        !last.done
      ) {
        msgs[msgs.length - 1] = { ...last, content: delta, done };
      } else {
        // 切换会话再切回来时占位消息已被清除，重新补一条流式占位
        msgs.push({ role: 'assistant', sessionKey, content: delta, done });
      }
      return { messages: msgs };
    }),

  finishMessage: (msg) =>
    set((s) => {
      const msgs = [...s.messages];
      // 优先：从末尾找最近一条同 session 的 done=false assistant 占位
      const placeholderIdx = msgs
        .map((m, i) => ({ m, i }))
        .filter(
          ({ m }) =>
            m.role === 'assistant' &&
            getSessionKey(m.sessionKey) === getSessionKey(msg.sessionKey) &&
            !m.done,
        )
        .pop()?.i;
      if (placeholderIdx !== undefined) {
        msgs[placeholderIdx] = msg;
        return { messages: msgs };
      }
      // 次选：没有占位时，若有同 idempotencyKey 的已完成消息则直接更新其内容
      // （处理 state:done 先于 state:final 到达的场景）
      if (msg.idempotencyKey) {
        const existingIdx = msgs.findIndex(
          (m) => m.done && m.idempotencyKey === msg.idempotencyKey,
        );
        if (existingIdx >= 0) {
          msgs[existingIdx] = msg;
          return { messages: msgs };
        }
      }
      msgs.push(msg);
      return { messages: msgs };
    }),

  clearMessages: () => set({ messages: [] }),

  remapSessionKey: (oldKey, newKey) =>
    set((s) => ({
      messages: s.messages.map((m) =>
        m.sessionKey === oldKey ? { ...m, sessionKey: newKey } : m,
      ),
    })),

  abortStreaming: (sessionKey) =>
    set((s) => {
      const msgs = [...s.messages];
      // 从末尾找最近一条同 session 的 done=false assistant 消息
      for (let i = msgs.length - 1; i >= 0; i--) {
        const m = msgs[i];
        if (
          m.role === 'assistant' &&
          getSessionKey(m.sessionKey) === getSessionKey(sessionKey) &&
          !m.done
        ) {
          msgs[i] = { ...m, done: true };
          break;
        }
      }
      return { messages: msgs };
    }),

  appendToolCall: (call) => set((s) => ({ toolCalls: [...s.toolCalls, call] })),

  updateToolCall: (toolCallId, update) =>
    set((s) => ({
      toolCalls: s.toolCalls.map((c) =>
        c.toolCallId === toolCallId ? { ...c, ...update } : c,
      ),
    })),

  clearToolCalls: () => set({ toolCalls: [] }),

  appendLifecycleEvent: (event) =>
    set((s) => ({ lifecycleEvents: [...s.lifecycleEvents, event] })),

  clearLifecycleEvents: () => set({ lifecycleEvents: [] }),

  setSessionsPopoverPinned: (pinned) => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(PIN_STORAGE_KEY, String(pinned));
    }
    set({ sessionsPopoverPinned: pinned });
  },
}));

export default useClawWsStore;
